import { useDispatch, useSelector } from "react-redux";
import { useCallback } from "react";
import {
  actualUIState,
  setHours,
  setInfoUser,
  setRolesByModule,
} from "../../SharedModule/redux/reducers/uiShared.reducer";
import { UsersService } from "../../UsersModule/services/users";
import { selectUserRoles } from "../redux/reducers/auth.reducer";
import { TimesheetsService } from "../../TimesModule/services/timesheets";
import { UsersProductsService } from "../../ProductsModule/services/users";

const PROFILE_CACHE_KEY = "hub_profile_cache";
const PROFILE_CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes

// Check for a fresh cached profile to avoid blocking on getUserProfileInfo
const getCachedProfile = (activeUserEmail: string) => {
  try {
    const raw = localStorage.getItem(PROFILE_CACHE_KEY);
    if (!raw) {
      return null;
    }
    const entry = JSON.parse(raw);
    if (
      entry.email !== activeUserEmail ||
      Date.now() - entry.cachedAt >= PROFILE_CACHE_TTL_MS
    ) {
      return null;
    }
    return entry.data;
  } catch {
    // ignore parse errors (corrupted storage, private mode)
    return null;
  }
};

const cacheProfile = (activeUserEmail: string, userInfo: any) => {
  try {
    localStorage.setItem(
      PROFILE_CACHE_KEY,
      JSON.stringify({
        email: activeUserEmail,
        data: userInfo,
        cachedAt: Date.now(),
      })
    );
  } catch {
    // ignore quota-exceeded or private-mode errors
  }
};

// No cache — fetch profile + hours in parallel, but don't let a failed
// profile lookup (ASUITE11-4384: user not synced yet, or ASuite Connector
// down) take the weekly hours down with it.
const fetchFreshUserInfo = async (activeUserEmail: string) => {
  const weeklyHoursPromise = TimesheetsService.getWeeklyHours();
  const userInfoPromise = UsersService.getUserProfileInfo(
    activeUserEmail
  ).catch((error) => {
    console.error(
      "Failed to load user profile info, degrading top navigation",
      error
    );
    return null;
  });

  const infoHours = await weeklyHoursPromise;
  let userInfo = await userInfoPromise;

  if (userInfo) {
    // Persist to cache for next page load
    cacheProfile(activeUserEmail, userInfo);
  } else {
    // Degraded profile: no photo (renders initials), no links/permissions.
    // Not cached, so we retry the real lookup on the next page load.
    userInfo = {
      photo: null,
      links: [],
      entryReminder: false,
      asuitePermissions: [],
      modulePermissions: {},
    };
  }

  return { infoHours, userInfo };
};

export const useTopNavigationInfo = () => {
  const dispatch = useDispatch();
  const { activeUserEmail } = useSelector(selectUserRoles);
  const {
    todayHours,
    weeklyHours,
    photo,
    links,
    modulePermissions,
    userRolesProductsModule,
  } = useSelector(actualUIState);

  const loadInfo = useCallback(async (shouldLoadUserInfo: boolean) => {
    if (!activeUserEmail) {
      return;
    }

    let infoHours, userInfo;
    if (shouldLoadUserInfo) {
      const cachedProfile = getCachedProfile(activeUserEmail);
      if (cachedProfile) {
        // Dispatch profile immediately from cache — sets isLoadingInfoUser=false
        // synchronously, then fetch weekly hours (real-time) separately
        dispatch(
          setInfoUser({
            photo: cachedProfile.photo,
            links: cachedProfile.links,
            entryReminder: cachedProfile.entryReminder,
            asuitePermissions: cachedProfile.asuitePermissions,
            modulePermissions: cachedProfile.modulePermissions,
          })
        );
        infoHours = await TimesheetsService.getWeeklyHours();
      } else {
        ({ infoHours, userInfo } = await fetchFreshUserInfo(activeUserEmail));
      }
    } else {
      infoHours = await TimesheetsService.getWeeklyHours();
    }

    if (infoHours.data) {
      dispatch(setHours(infoHours.data));
    }
    if (userInfo && shouldLoadUserInfo) {
      dispatch(
        setInfoUser({
          photo: userInfo.photo,
          links: userInfo.links,
          entryReminder: userInfo.entryReminder,
          asuitePermissions: userInfo.asuitePermissions,
          modulePermissions: userInfo.modulePermissions,
        })
      );
    }
  }, []);

  // ASUITE11-2806: could use new endpoint in auth.tsx and delete roles products module
  const loadUserInfo = useCallback(async () => {
    const userRolesProductsModule =
      await UsersProductsService.getUserRolesForProductsModule();

    if (userRolesProductsModule?.data) {
      dispatch(
        setRolesByModule({
          productsRoles: userRolesProductsModule.data,
        })
      );
    }
  }, []);

  return {
    todayHours,
    weeklyHours,
    photo,
    links,
    modulePermissions,
    userRolesProductsModule,
    loadInfo,
    loadUserInfo,
  };
};
